import React, { useCallback, useEffect, useState } from 'react';
import { Card, Descriptions, Tag, Button, Spin } from 'antd';
import callApi from '../../Api/config';
import ReviewPaymentModal from '../../components/ReviewPaymentModal';
import { toastError } from '../../service/Toast';

interface AgentOrderPaymentDetailsProps {
  partnerId: string;
  order: any;
  onPaymentUpdated?: () => void;
}

const resolveReceiptUrl = (receiptUrl: string) =>
  receiptUrl.startsWith('http')
    ? receiptUrl
    : `https://${process.env.REACT_APP_S3_BUCKET || 'documitra-vault'}.s3.amazonaws.com/${receiptUrl}`;

const AgentOrderPaymentDetails: React.FC<AgentOrderPaymentDetailsProps> = ({
  partnerId,
  order,
  onPaymentUpdated,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [payments, setPayments] = useState<any[]>([]);
  const [verifying, setVerifying] = useState(false);

  // Review Payment modal state
  const [reviewModalVisible, setReviewModalVisible] = useState(false);
  const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(null);

  // Calculate order amount
  const memberCount = order?.members?.length || 0;
  const customPrice = order?.partnerPackage?.customPrice;
  const basePrice = order?.partnerPackage?.package?.basePrice;
  const orderAmount =
    customPrice !== undefined && customPrice !== null
      ? Number(customPrice)
      : basePrice !== undefined && basePrice !== null
        ? Number(basePrice)
        : 0;
  const totalOrderAmount = orderAmount * memberCount;

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await callApi(
        `/partner-portal/admin/payments?partnerId=${partnerId}&page=1&limit=500`,
        'GET'
      );
      if (res?.status) {
        const rawData = res?.data?.data || res?.data;
        const items = Array.isArray(rawData) ? rawData : rawData?.results || rawData?.items || [];
        setPayments(items.filter((p: any) => p.groupIds?.includes(order?.id)));
      }
    } catch (error) {
      console.error('Error fetching order payments:', error);
      toastError('Failed to fetch payment details.');
    } finally {
      setLoading(false);
    }
  }, [partnerId, order?.id]);

  useEffect(() => {
    if (!partnerId || !order?.id) return;
    fetchPayments();
  }, [partnerId, order?.id, fetchPayments]);

  const handleOpenReview = (paymentId: string) => {
    setSelectedPaymentId(paymentId);
    setReviewModalVisible(true);
  };

  const getPaymentReviewStatus = (payment: any): { color: string; label: string } => {
    const reviewStatus = (payment?.reviewStatus || payment?.status || '').toLowerCase();
    if (
      reviewStatus.includes('paid') ||
      reviewStatus.includes('payment_done') ||
      reviewStatus.includes('payment-done') ||
      reviewStatus === 'succeeded'
    ) {
      return { color: 'success', label: 'Paid' };
    }
    if (reviewStatus.includes('partial')) {
      return { color: 'warning', label: 'Partial Payment Received' };
    }
    if (reviewStatus.includes('payment_verification_pending') || reviewStatus === 'pending') {
      return { color: 'warning', label: 'Payment Confirmation Pending' };
    }
    if (
      reviewStatus.includes('payment_unsuccessful') ||
      reviewStatus.includes('failed') ||
      reviewStatus.includes('rejected')
    ) {
      return { color: 'error', label: 'Payment Unsuccessful' };
    }
    return { color: 'processing', label: 'Pending Review' };
  };

  const isPaymentPending = (payment: any): boolean => {
    if (payment?.verifiedAt) return false;
    const method = String(payment?.provider || payment?.paymentMethod || '').toLowerCase();
    const isStripe = method.includes('stripe') || Boolean(payment?.stripePaymentIntentId);
    if (isStripe) return false;
    if (!method.includes('zelle') && !method.includes('venmo')) return false;
    const s = (payment?.status || '').toLowerCase();
    return s.includes('payment_verification_pending') || s === 'pending' || s === 'payment_pending';
  };

  return (
    <div style={{ padding: '24px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
        }}
      >
        <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600 }}>
          Payment Details - {order?.groupCode || order?.id}
        </h2>
        <Button onClick={fetchPayments} icon={<i className="fa fa-refresh" />}>
          Refresh
        </Button>
      </div>

      <Card
        title="Order Summary"
        size="small"
        style={{
          marginBottom: '20px',
          borderRadius: '8px',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
          border: 'none',
        }}
      >
        <Descriptions column={2} bordered size="small">
          <Descriptions.Item label="Order ID">{order?.id || '--'}</Descriptions.Item>
          <Descriptions.Item label="Group Code">{order?.groupCode || '--'}</Descriptions.Item>
          <Descriptions.Item label="Group Name">{order?.groupName || '--'}</Descriptions.Item>
          <Descriptions.Item label="Order Status">{order?.status || '--'}</Descriptions.Item>
          <Descriptions.Item label="Members">{memberCount}</Descriptions.Item>
          <Descriptions.Item label="Order Total">${totalOrderAmount.toFixed(2)}</Descriptions.Item>
        </Descriptions>
      </Card>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
        </div>
      ) : payments.length === 0 ? (
        <Card
          style={{
            borderRadius: '8px',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
            border: 'none',
            textAlign: 'center',
            color: 'gray',
          }}
        >
          No payment records found for this order.
        </Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {payments.map((payment, idx) => {
            const reviewColor = getPaymentReviewStatus(payment);
            const pending = isPaymentPending(payment);
            return (
              <Card
                key={payment.id}
                title={`Payment ${idx + 1}`}
                size="small"
                extra={
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <Tag color={reviewColor.color}>{reviewColor.label}</Tag>
                    {pending && (
                      <Button
                        type="primary"
                        size="small"
                        onClick={() => handleOpenReview(payment.id)}
                        loading={verifying}
                      >
                        Review
                      </Button>
                    )}
                  </div>
                }
                style={{
                  borderRadius: '8px',
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                  border: 'none',
                }}
              >
                <Descriptions column={2} bordered size="small">
                  <Descriptions.Item label="Payment ID">{payment.id}</Descriptions.Item>
                  <Descriptions.Item label="Transaction ID">
                    {payment.transactionId || payment.stripePaymentIntentId || '--'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Amount">
                    ${Number(payment.amount || 0).toFixed(2)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Net Received">
                    ${Number(payment.netReceived || 0).toFixed(2)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Partial Received">
                    ${Number(payment.partialReceived || 0).toFixed(2)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Payment Method">
                    {payment.paymentMethod || payment.provider || '--'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Created At">
                    {payment.createdAt ? new Date(payment.createdAt).toLocaleString() : '--'}
                  </Descriptions.Item>
                  {payment.verifiedAt && (
                    <Descriptions.Item label="Reviewed At">
                      {new Date(payment.verifiedAt).toLocaleString()}
                    </Descriptions.Item>
                  )}
                  {payment.receiptUrl && (
                    <Descriptions.Item label="Receipt">
                      <a
                        href={
                          payment.receiptUrl.startsWith('http')
                            ? payment.receiptUrl
                            : `https://${process.env.REACT_APP_S3_BUCKET || 'documitra-vault'}.s3.amazonaws.com/${payment.receiptUrl}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#1890ff' }}
                      >
                        View Receipt
                      </a>
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Card>
            );
          })}
        </div>
      )}

      {/* Review Payment Modal */}
      <ReviewPaymentModal
        visible={reviewModalVisible}
        paymentId={selectedPaymentId || ''}
        orderAmount={totalOrderAmount}
        onClose={() => {
          setReviewModalVisible(false);
          setSelectedPaymentId(null);
        }}
        onSuccess={onPaymentUpdated || (() => {})}
      />
    </div>
  );
};

export default AgentOrderPaymentDetails;
